import * as rd from '@devexperts/remote-data-ts'
import { Button } from '@spiaggeit/spit-ui'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { CartFooter } from '@/components/CartFooter'
import { LoaderTrigger } from '@/components/LoaderTrigger'
import { SelectedElementDrawer } from '@/components/SelectedElementDrawer'
import { useAppDispatch, useAppSelector } from '@/hooks/store.ts'
import { useFetchPricePreview } from '@/hooks/useFetchPricePreview.ts'
import { useResetInsertInsuranceProducts } from '@/hooks/useResetInsertInsurance'
import { LastSpotDialog } from '@/routes/Map/LastSpotDialog.tsx'
import { UnavailableSpotsDialog } from '@/routes/Map/UnavailableSpotsDialog.tsx'
import { useMapUtils } from '@/routes/Map/useMapUtils.ts'
import { BookingAvailability } from '@/store/bookingAvailabilitySlice'
import { cartSlice } from '@/store/cartSlice/index.ts'
import { cartItemsIdsSelector } from '@/store/cartSlice/selectors/cartItemsIdsSelector'
import { extraSlice } from '@/store/extraSlice.ts'
import { licenseSlice } from '@/store/licenseSlice'
import { mapSelectors, mapSlice } from '@/store/mapSlice.ts'

import { servicesSlice } from '@/store/servicesSlice'
import { NO, YES } from '@/utils/constants'
import { FirstVisitDialog } from './FirstVisitDialog'
import { LegendDialog } from './LegendDialog'

export const MapRoute = () => {
  const { t } = useTranslation()
  const mapUtils = useMapUtils()
  const dispatch = useAppDispatch()

  const license = useAppSelector(licenseSlice.selectors.license)
  const cartItemsIds = useAppSelector(cartItemsIdsSelector)
  const mapRemoteData = useAppSelector(mapSelectors.mapRemoteData)
  const services = useAppSelector(servicesSlice.selectors.services)
  const weatherInsurancePrice = useAppSelector(
    extraSlice.selectors.weatherInsurancePricePreview
  )
  const selectedElement = useAppSelector(mapSelectors.selectedElement)
  const [isUnavailableSpotsDialogOpen, setIsUnavailableSpotsDialogOpen] =
    useState(false)
  const [isLastSpotDialogOpen, setIsLastSpotDialogOpen] = useState(false)
  const areRequestsSuccessful =
    rd.isSuccess(services) &&
    rd.isSuccess(mapRemoteData) &&
    rd.isSuccess(weatherInsurancePrice)

  const { resetInsertInsuranceProducts } = useResetInsertInsuranceProducts()

  useFetchPricePreview()

  useEffect(() => {
    /* Load initial map data required for the page */
    mapUtils.loadInitialData()

    /*resets previosly added extra services and weather insurance
    (in case user is coming back from 'insertInsurance' page)*/
    resetInsertInsuranceProducts()

    dispatch(mapSlice.actions.loadWarehouse())
  }, [])

  const hasPushedDataLayer = useRef(false)

  useEffect(() => {
    if (
      !hasPushedDataLayer.current &&
      license &&
      rd.isSuccess(weatherInsurancePrice)
    ) {
      window.dataLayer.push({
        'Assicurabile meteo': weatherInsurancePrice.value > 0 ? YES : NO,
        Citta: license?.beach.city,
        Provincia: license?.beach.state,
        Regione: license?.beach.region,
        Stabilimento: license?.beach.name ?? license?.license,
        Subregion: license?.beach.subregion,
        event: 'insert_map',
      })
      hasPushedDataLayer.current = true
    }
  }, [license, weatherInsurancePrice])

  useEffect(() => {
    if (areRequestsSuccessful) {
      mapUtils.initMap()
    }
  }, [areRequestsSuccessful])

  useEffect(() => {
    if (!areRequestsSuccessful || !mapUtils.isCanvasReady) return
    mapUtils.highlightSelectedItems()
  }, [mapUtils.isCanvasReady, areRequestsSuccessful, cartItemsIds])

  useEffect(() => {
    if (!mapUtils.isCanvasReady) return

    /* Reset canvas height on resize to force recalculations */
    window.addEventListener('resize', mapUtils.resetCanvasHeightHandler)

    return () =>
      window.removeEventListener('resize', mapUtils.resetCanvasHeightHandler)
  }, [mapUtils.isCanvasReady])

  useEffect(() => {
    /* Open dialogs depending on the number of spots available */
    if (!areRequestsSuccessful || !mapUtils.isCanvasReady) return
    if (mapUtils.availableSpots.areNone) {
      setIsUnavailableSpotsDialogOpen(true)
    } else if (mapUtils.availableSpots.isOnlyOne) {
      setIsLastSpotDialogOpen(true)
      setIsUnavailableSpotsDialogOpen(false)
    } else if (mapUtils.availableSpots.areMoreThanOne) {
      setIsUnavailableSpotsDialogOpen(false)
      setIsLastSpotDialogOpen(false)
    }
  }, [
    areRequestsSuccessful,
    mapUtils.isCanvasReady,
    mapUtils.availableSpots.areNone,
    mapUtils.availableSpots.areMoreThanOne,
    mapUtils.availableSpots.isOnlyOne,
  ])

  useEffect(() => {
    dispatch(cartSlice.actions.setBookingType(BookingAvailability.SPOTS))
  }, [])

  const handleSelectLastSpot = () => {
    if (!mapUtils.availableSpots.array) return
    dispatch(
      mapSlice.actions.selectMapElement(mapUtils.availableSpots.array[0])
    )
    setIsLastSpotDialogOpen(false)
  }

  if (!areRequestsSuccessful) {
    return <LoaderTrigger message={t('map.loading')} />
  }

  return (
    <>
      <main className="relative flex flex-1 flex-col">
        <div className="max-w-full flex-1 overflow-hidden" id="canvas"></div>

        {selectedElement ? <SelectedElementDrawer /> : null}

        <span className="fixed left-4 top-32 z-20 lg:hidden">
          <LegendDialog />
        </span>

        {!mapUtils.userHasVisitedMapBefore &&
          mapUtils.availableSpots.areMoreThanOne && <FirstVisitDialog />}

        <UnavailableSpotsDialog
          isOpen={isUnavailableSpotsDialogOpen}
          setIsOpen={setIsUnavailableSpotsDialogOpen}
        />

        <LastSpotDialog
          isOpen={isLastSpotDialogOpen}
          setIsOpen={setIsLastSpotDialogOpen}
        >
          <Button onClick={handleSelectLastSpot}>
            {t('map.lastAvailableSpotDialog.seeAvailableElement')}
          </Button>
        </LastSpotDialog>
      </main>

      <CartFooter zoomIn={mapUtils.zoomIn} zoomOut={mapUtils.zoomOut} />
    </>
  )
}
